import { getTextWidth } from '@thoughtspot/custom-style-service';
import { create } from '@thoughtspot/logger';
import { formatDataValue, getValueFormattedByConfig, UNITS_TO_DIVIDING_FACTOR, } from '@thoughtspot/number-format-util';
import { flipTooltipToContextMenuEnabled, gradientBufferEnabled, } from '@thoughtspot/session-service';
import { assign } from '@thoughtspot/string-util';
import _ from 'lodash';
import { decimalsForFormat } from '../../formatting-util';
import { CategoryType, Unit } from '../../types/prism';
import { truncate } from '../../utils/string';
import { chartTypeToDatalabelFormatter } from '../chart-type-formatter-util';
import { extractCaptionTitleAndLink, formatInfinityValue, formatXAxisLabel, formatYAxisLabel, getDataLabelFromColumn, getLabelForNumericAxisOrColumn, getLocaleName, getStackLabelFormatting, getYAxisLabelForAttribute, } from '../formatter-service';
import { genericTooltipData, getClassByLabelType, tooltipComponent, } from '../tooltip-utils';
import { AXIS_TYPES } from '../types';
import { CUSTOM_TOOLTIP_TEMPLATE, CUSTOM_TOOLTIP_TEMPLATE_WITH_ACCENT_COLOR, formatterTemplates, INVISIBLE_TOOLTIP, } from './highchart-formatter-templates';
const MAX_AXIS_TICKS = 100;
const MIN_DISTANCE_TICK_THRESHOLD = 0.2;
const DEFAULT_TOOLTIP_BORDER_WIDTH = 1;
const logger = create('blink-core: highchart-formatter');
export function getTooltipSeriesInfo() {
    const isHandleMissingValuesEnabled = this.series.chart.userOptions
        .isHandleMissingValuesEnabled;
    const point = this.point || {};
    const xValue = point.xRawValues?.[0] === null && isHandleMissingValuesEnabled
        ? point.xRawValues[0]
        : this.x || point.x;
    const seriesInfo = {
        x: xValue,
        y: this.y || point.y,
        z: this.z || point.z,
        seriesName: this.series.name,
        categoryName: point.categoryName,
        seriesColor: this.series.color,
        tooltipFormatter: this.series.userOptions.tooltipFormatter,
        valueColumnIdentifier: this.series.userOptions.valueColumnIdentifier,
        open: point.open,
        high: point.high,
        low: point.low,
        close: point.close,
        tooltipDataContent: point.tooltipDataContent,
    };
    return seriesInfo;
}
export const getUnitFromFormat = (format) => {
    let unit;
    if (format.category === CategoryType.Number) {
        unit = format.numberFormatConfig.unit;
    }
    else if (format.category === CategoryType.Currency) {
        unit = format.currencyFormatConfig.unit;
    }
    else {
        unit = Unit.None;
    }
    return unit;
};
export function dataLabelFormatter() {
    const props = this.series.chart.userOptions.chart
        .props;
    const customFormatter = chartTypeToDatalabelFormatter[props.chartType];
    if (customFormatter) {
        return customFormatter.call(this);
    }
    const valueColumnIdentifier = this.series.userOptions
        .valueColumnIdentifier;
    if (_.isNil(valueColumnIdentifier)) {
        return undefined;
    }
    const seriesColumn = props.allColumns[valueColumnIdentifier];
    if (!seriesColumn.showDataLabels) {
        return undefined;
    }
    const dataLabel = getDataLabelFromColumn(seriesColumn, this.y, props.yValueToRawValues);
    const maxCharacters = this.series.userOptions?.dataLabels?.maxCharacters;
    if (_.isNumber(maxCharacters)) {
        return truncate(dataLabel, maxCharacters);
    }
    return dataLabel;
}
export function xAxisLabelFormatter() {
    const axis = this.axis;
    const props = axis.chart.userOptions.chart.props;
    const isHandleMissingValuesEnabled = this.chart.userOptions
        .isHandleMissingValuesEnabled;
    let val = this.value;
    if (this.isFirst &&
        this.value &&
        this.chart.xAxis[0].dataMin === this.value &&
        props.xValueToRawValues[0]?.[0].value === null &&
        isHandleMissingValuesEnabled) {
        val = null;
    }
    const valueAtX = props.xValueToRawValues[val];
    const captionAndLink = valueAtX && extractCaptionTitleAndLink(valueAtX);
    if (axis.userOptions?.labels?.maxCharacters) {
        props.maxCharacters = _.toNumber(axis.userOptions.labels.maxCharacters);
    }
    return formatXAxisLabel(val, axis.userOptions.id, axis.tickPositions, axis.tickInterval, {
        axisMin: axis.min,
        axisMax: axis.max,
        dataMin: axis.dataMin,
        dataMax: axis.dataMax,
    }, axis.userOptions.showInfinity, axis.userOptions.showMinusInfinity, axis.userOptions.infinityPlaceHolder, captionAndLink, props);
}
export function yAxisLabelFormatter() {
    const axis = this.axis;
    const props = axis.chart.userOptions.chart.props;
    const yAxisColumns = props.yAxisColumns.map(colId => props.allColumns[colId]);
    const axisClientStateProps = props.axes[axis.userOptions.id];
    const isStackedYAxisColumn = _.includes(axisClientStateProps.linkedColumns, props.yAxisColumns[0]);
    const yAxis = props.axes[axis.userOptions.id];
    if (props.isYAxisStackedAsPercent && isStackedYAxisColumn) {
        return formatDataValue(this.value / 100, {
            nDecimal: parseInt(_.get(props.axes[axis.userOptions.id], 'formatting.percentageFormatConfig.decimals', 2), 10),
            isPercent: true,
        });
    }
    let yValueToRawValues = props.yValueToRawValues;
    const isAugmentQuery = () => {
        const queryIndex = 1;
        return !!props?.queryToColumnMap && !!props.queryToColumnMap[queryIndex]
            ? props.queryToColumnMap[queryIndex].some(colId => colId === yAxis.linkedColumns[0])
            : false;
    };
    if (isAugmentQuery()) {
        yValueToRawValues = props?.augmentYValueToRawValues;
    }
    let maxCharacters;
    if (axis.userOptions?.labels?.maxCharacters) {
        maxCharacters = _.toNumber(axis.userOptions.labels.maxCharacters);
    }
    if (yAxisColumns.length > 1 ||
        yAxisColumns[0].isColumnValidForNumberFormatting) {
        const currentLabel = formatYAxisLabel(this.value, yAxis, axis.tickPositions, axis.tickInterval, props.allColumns[yAxis.linkedColumns[0]], {
            axisMin: axis.min,
            axisMax: axis.max,
        }, axis.userOptions.showInfinity, axis.userOptions.showMinusInfinity, axis.userOptions.infinityPlaceholderValue, yValueToRawValues, maxCharacters);
        if (axis.tickPositions.length === 2 && this.isLast) {
            const firstLabel = formatYAxisLabel(axis.tickPositions[0], yAxis, axis.tickPositions, axis.tickInterval, props.allColumns[yAxis.linkedColumns[0]], {
                axisMin: axis.min,
                axisMax: axis.max,
            }, axis.userOptions.showInfinity, axis.userOptions.showMinusInfinity, axis.userOptions.infinityPlaceholderValue, yValueToRawValues, maxCharacters);
            if (firstLabel === currentLabel) {
                return '';
            }
        }
        return currentLabel;
    }
    return getYAxisLabelForAttribute(yAxisColumns[0], this.value, props.yValueToRawValues[yAxisColumns[0].dataRowIndex][this.value]);
}
export function stackLabelFormatter() {
    const axis = this.axis;
    const xAxis = axis.chart.xAxis[0];
    const stacks = axis.stacks || axis.stacking.stacks;
    const currentX = this.x;
    let total = this.total;
    if (xAxis.min > currentX || xAxis.max < currentX) {
        return '';
    }
    const positiveColumn = Object.entries(stacks).find(([key]) => key.startsWith('column'))?.[1];
    const negativeColumn = Object.entries(stacks).find(([key]) => key.startsWith('-column'))?.[1];
    if (total >= 0) {
        if (!!negativeColumn && !!negativeColumn[currentX]) {
            total += negativeColumn[currentX].total;
            if (total < 0)
                return '';
        }
    }
    else if (!!positiveColumn && !!positiveColumn[currentX]) {
        total += positiveColumn[currentX].total;
        if (total >= 0)
            return '';
    }
    const props = axis.chart.userOptions.chart.props;
    const allYAxisColumns = props.yAxisColumns.map((colId) => props.allColumns[colId]);
    const columns = allYAxisColumns.filter((col) => props.axes[axis.userOptions.id].linkedColumns.find((colId) => colId === col.id));
    const column = columns[0];
    const formatPattern = column.formatPattern;
    const columnLocale = getLocaleName(column);
    const format = getStackLabelFormatting(props.allColumns, props.yAxisColumns, props.axes[axis.userOptions.id]);
    if (format) {
        return getValueFormattedByConfig(total, format, {
            formatPattern,
            columnLocale,
        });
    }
    const yAxisColumns = props.yAxisColumns;
    let maxCharacters;
    if (axis.userOptions?.labels?.maxCharacters) {
        maxCharacters = _.toNumber(axis.userOptions.labels.maxCharacters);
    }
    const formattedYAxisLabel = formatYAxisLabel(total, props.axes[axis.userOptions.id], axis.tickPositions, axis.tickInterval, yAxisColumns[0], {
        axisMin: axis.min,
        axisMax: axis.max,
    }, axis.max, axis.min, axis.max, props._yValueToRawValues, maxCharacters);
    if (yAxisColumns.length > 1) {
        return assign(formatterTemplates.stackLabelForMultipleY, formattedYAxisLabel);
    }
    if (yAxisColumns[0].isEffectivelyNumeric && !props.isYAxisOrdinalBased) {
        return formattedYAxisLabel;
    }
    return getYAxisLabelForAttribute(yAxisColumns[0], this.value, props.yValueToRawValues);
}
export function toolTipFormatter() {
    const props = this.series.chart.userOptions.chart.props;
    const customOverrides = props.advancedProperties?.tooltip;
    if (!this.series.tooltipOptions.enable ||
        customOverrides?.enabled === false) {
        return INVISIBLE_TOOLTIP;
    }
    const seriesInfo = getTooltipSeriesInfo.call(this);
    let tooltipData = genericTooltipData(seriesInfo, props);
    let hasCustomBlockStyle = false;
    if (this.point.additionalTooltipData) {
        tooltipData = tooltipData.concat(this.point.additionalTooltipData);
        hasCustomBlockStyle = _.some(this.point.additionalTooltipData, obj => _.has(obj, 'customBlockStyle'));
    }
    if (!tooltipData || !tooltipData.length) {
        return false;
    }
    const finalLabel = _.map(tooltipData, dataObject => tooltipComponent({
        ...dataObject,
        ...getClassByLabelType(dataObject.labelType, dataObject, customOverrides),
        customOverrides,
        ...(hasCustomBlockStyle
            ? { customBlockClass: ' chart-tooltip-block-override' }
            : {}),
    })).join('');
    let toolTipStyle = '';
    if (customOverrides) {
        const { backgroundColor, opacity } = customOverrides || {};
        if (opacity === '0') {
            return INVISIBLE_TOOLTIP;
        }
        toolTipStyle += backgroundColor
            ? `background-color:${backgroundColor};`
            : '';
        toolTipStyle +=
            !_.isNil(opacity) && opacity !== ''
                ? `opacity:${opacity / 100};`
                : '';
    }
    let overrideClass = '';
    if (hasCustomBlockStyle) {
        overrideClass = ' custom-tooltip-override';
    }
    if (flipTooltipToContextMenuEnabled() && !customOverrides) {
        return assign(CUSTOM_TOOLTIP_TEMPLATE_WITH_ACCENT_COLOR, DEFAULT_TOOLTIP_BORDER_WIDTH, this.point?.color || this.series.color, toolTipStyle, finalLabel, overrideClass);
    }
    return assign(CUSTOM_TOOLTIP_TEMPLATE, customOverrides?.borderWidth || DEFAULT_TOOLTIP_BORDER_WIDTH, customOverrides?.borderColor || this.point?.color || this.series.color, toolTipStyle, finalLabel);
}
export const findModuloDecimal = (tickInterval, minInterval) => {
    if (!tickInterval) {
        return 0;
    }
    tickInterval = tickInterval.toString();
    minInterval = minInterval.toString();
    if (tickInterval.includes('.') && minInterval.includes('.')) {
        return Math.max(tickInterval.split('.')[1].length, minInterval.split('.')[1].length);
    }
    if (tickInterval.includes('.') && !minInterval.includes('.')) {
        return tickInterval.split('.')[1].length;
    }
    if (!tickInterval.includes('.') && minInterval.includes('.')) {
        return minInterval.split('.')[1].length;
    }
    return 0;
};
export const tickMin = (dataMin, intervalPrime, decimals) => {
    let minTick;
    if (dataMin === 0) {
        return 0;
    }
    const moduloDecimal = findModuloDecimal(dataMin, intervalPrime);
    const remainder = parseFloat((Math.abs(dataMin) % intervalPrime).toFixed(moduloDecimal));
    if (remainder === 0 && dataMin !== 0) {
        minTick = parseFloat(dataMin.toFixed(moduloDecimal));
    }
    else if (dataMin > 0) {
        minTick = parseFloat((dataMin - remainder + intervalPrime).toFixed(decimals));
    }
    else {
        minTick = parseFloat((dataMin + remainder).toFixed(decimals));
    }
    return minTick;
};
const getMinTickPosition = (dataMin, minInterval, intervalPrime, decimals, rangeMin, hasMultipleTicks, configOptions) => {
    if (!_.isNil(rangeMin)) {
        const remainderPrime = rangeMin % intervalPrime;
        const absRemainderPrime = remainderPrime < 0
            ? remainderPrime + intervalPrime
            : remainderPrime;
        if (intervalPrime - absRemainderPrime >= minInterval &&
            (intervalPrime - absRemainderPrime) / intervalPrime >
                MIN_DISTANCE_TICK_THRESHOLD) {
            const remainder = rangeMin % minInterval;
            const firstTick = rangeMin < 0 && remainder !== 0
                ? rangeMin - remainder - minInterval
                : rangeMin - remainder;
            return parseFloat(firstTick.toFixed(decimals));
        }
    }
    else if (hasMultipleTicks || configOptions?.useDefaultMinTick) {
        const remainder = dataMin % intervalPrime;
        const firstTick = dataMin < 0 && remainder !== 0
            ? dataMin - remainder - intervalPrime
            : dataMin - remainder;
        return parseFloat(firstTick.toFixed(decimals));
    }
    return null;
};
const getMaxTickPosition = (dataMax, minInterval, intervalPrime, decimals, rangeMax, hasMultipleTicks, configOptions) => {
    if (!_.isNil(rangeMax)) {
        const remainderPrime = rangeMax % intervalPrime;
        const absRemainderPrime = remainderPrime > 0
            ? remainderPrime
            : remainderPrime + intervalPrime;
        if (absRemainderPrime >= minInterval &&
            absRemainderPrime / intervalPrime > MIN_DISTANCE_TICK_THRESHOLD) {
            const remainder = rangeMax % minInterval;
            const lastTick = rangeMax > 0 && remainder !== 0
                ? rangeMax - remainder + minInterval
                : rangeMax - remainder;
            return parseFloat(lastTick.toFixed(decimals));
        }
    }
    else if (hasMultipleTicks || configOptions?.useDefaultMinTick) {
        const remainder = dataMax % intervalPrime;
        const lastTick = dataMax > 0 && remainder !== 0
            ? dataMax - remainder + intervalPrime
            : dataMax - remainder;
        return parseFloat(lastTick.toFixed(decimals));
    }
    return null;
};
export const tickPositionsForNumericYAxis = (dataMin, dataMax, initialTickInterval, minInterval, showInfinity, showMinusInfinity, infinityPlaceholderValue, axisName, decimals, axisRange, configOptions) => {
    if (_.isNil(dataMin) && _.isNil(dataMax)) {
        logger.warn('tickPositionsForNumericYAxis: both dataMin and dataMax are undefined, please check the data.');
        return [0];
    }
    let positionedTicks = [];
    const uniqueLabelsForInfinity = new Set();
    let tickInterval = initialTickInterval;
    const numberOfTicks = (dataMax - dataMin) / tickInterval;
    if (MAX_AXIS_TICKS < numberOfTicks) {
        tickInterval = (dataMax - dataMin) / MAX_AXIS_TICKS;
    }
    const moduloDecimals = findModuloDecimal(tickInterval, minInterval);
    const moduloInterval = parseFloat((tickInterval % minInterval).toFixed(moduloDecimals));
    let intervalPrime = moduloInterval !== 0
        ? tickInterval - moduloInterval + minInterval
        : tickInterval;
    if (intervalPrime === 0) {
        intervalPrime = minInterval;
    }
    for (let i = tickMin(dataMin, intervalPrime, decimals); i <= dataMax; i += intervalPrime) {
        while (dataMin + intervalPrime === dataMin) {
            intervalPrime *= 10;
        }
        const infinityLabel = formatInfinityValue(i, tickInterval, showInfinity, showMinusInfinity, dataMin, dataMax, infinityPlaceholderValue, axisName);
        if (infinityLabel) {
            if (!uniqueLabelsForInfinity.has(infinityLabel)) {
                positionedTicks.push(parseFloat(i.toFixed(decimals)));
                uniqueLabelsForInfinity.add(infinityLabel);
            }
        }
        else {
            positionedTicks.push(parseFloat(i.toFixed(decimals)));
        }
    }
    const lastValue = positionedTicks[positionedTicks.length - 1];
    if (lastValue < dataMax) {
        const maxTick = getMaxTickPosition(dataMax, minInterval, intervalPrime, decimals, axisRange?.max, positionedTicks.length > 1, configOptions);
        if (!_.isNil(maxTick)) {
            positionedTicks.push(maxTick);
        }
    }
    const firstValue = positionedTicks[0];
    if (firstValue > dataMin) {
        const minTick = getMinTickPosition(dataMin, minInterval, intervalPrime, decimals, axisRange?.min, positionedTicks.length > 1, configOptions);
        if (!_.isNil(minTick)) {
            positionedTicks = [minTick, ...positionedTicks];
        }
    }
    if (positionedTicks.length === 0) {
        return [0];
    }
    return positionedTicks;
};
export const minIntervalForTickPositions = (format, unit, decimals) => {
    let minInterval;
    const autoDividingFactor = 1;
    if (format.category === CategoryType.Number ||
        format.category === CategoryType.Currency) {
        const dividingFactor = unit === Unit.Auto
            ? autoDividingFactor
            : UNITS_TO_DIVIDING_FACTOR[unit];
        minInterval = dividingFactor / Math.pow(10, decimals);
    }
    else if (format.category === CategoryType.Percentage) {
        minInterval = Math.pow(10, 0) / Math.pow(10, decimals);
    }
    else {
        minInterval = Math.pow(10, 0) / Math.pow(10, decimals);
    }
    return minInterval;
};
export function getUpdatedAxisRangeBasedOnTicks(axisIdx, props, range, highchartAxis) {
    if (_.isNil(range.min) && _.isNil(range.max)) {
        logger.warn('range should not be completely null. Please check the application flow', range, props.axes[axisIdx]);
        return null;
    }
    const axis = props.axes[axisIdx];
    const axisName = axis.axisType;
    const format = axis.formatting;
    const unit = getUnitFromFormat(format);
    const decimals = decimalsForFormat(format, unit);
    const minInterval = minIntervalForTickPositions(format, unit, decimals);
    let rangeMax = range.max;
    if (_.isNil(rangeMax)) {
        if (highchartAxis.dataMax < 0 && range.min < 0) {
            rangeMax = 0;
        }
        else {
            rangeMax = _.max([highchartAxis.dataMax, range.min]);
        }
    }
    let rangeMin = range.min;
    if (_.isNil(rangeMin)) {
        if (highchartAxis.dataMin > 0 && range.max > 0) {
            rangeMin = 0;
        }
        else {
            rangeMin = _.min([highchartAxis.dataMin, 0]);
        }
    }
    const tickPositions = tickPositionsForNumericYAxis(rangeMin, rangeMax, highchartAxis.tickInterval, minInterval, highchartAxis.userOptions.showInfinity, highchartAxis.userOptions.showMinusInfinity, highchartAxis.userOptions.infinityPlaceholderValue, axisName, decimals, range);
    const maxTickPos = tickPositions[tickPositions.length - 1];
    const minTickPos = tickPositions[0];
    const useMinTickPos = rangeMax < maxTickPos && tickPositions.length !== 1;
    const useMaxTickPos = rangeMin > minTickPos && tickPositions.length !== 1;
    return {
        max: useMinTickPos ? maxTickPos : rangeMax,
        min: useMaxTickPos ? minTickPos : rangeMin,
    };
}
export const handleTicksOverlap = (positionedTicks, availableHeight, dataMin, fontSize) => {
    const ticksHeightTotal = positionedTicks.length * parseInt(fontSize, 10);
    const AXIS_HEIGHT_THRESHOLD = 10;
    if (availableHeight < AXIS_HEIGHT_THRESHOLD) {
        positionedTicks = [dataMin];
    }
    else if (availableHeight < ticksHeightTotal) {
        positionedTicks = null;
    }
    return positionedTicks;
};
export function getUniqueTickPositionsForAxis(axisName, configOptions) {
    const props = this.chart.userOptions.chart.props;
    let positionedTicks = [];
    const axisType = axisName === AXIS_TYPES.Y ? 'yAxisColumns' : 'xAxisColumns';
    const axisColumns = props[axisType].map((colId) => props.allColumns[colId]);
    if (axisColumns.length > 1 || axisColumns[0].isEffectivelyNumeric) {
        if (axisName === AXIS_TYPES.Y && axisColumns[0].isEffectivelyNumeric) {
            let dataMax = this.max;
            let dataMin = this.min;
            const format = props.axes[this.userOptions.id].formatting;
            const axisRange = props.axes[this.userOptions.id].yAxisRange;
            if (dataMax < 0 &&
                dataMin < 0 &&
                _.isNil(axisRange?.max) &&
                !configOptions?.useDefaultMinTick) {
                dataMax = 0;
            }
            else if (dataMin > 0 &&
                dataMax > 0 &&
                _.isNil(axisRange?.min) &&
                !configOptions?.useDefaultMinTick) {
                dataMin = 0;
            }
            const unit = getUnitFromFormat(format);
            const decimals = decimalsForFormat(format, unit);
            const minInterval = minIntervalForTickPositions(format, unit, decimals);
            positionedTicks = [
                ...tickPositionsForNumericYAxis(dataMin, dataMax, this.tickInterval, minInterval, this.userOptions.showInfinity, this.userOptions.showMinusInfinity, this.userOptions.infinityPlaceholderValue, axisName, decimals, axisRange, configOptions),
            ];
        }
        else {
            const uniqueLabels = new Set();
            this.tickPositions.forEach((tick) => {
                const valueAtX = props.xValueToRawValues[this.value];
                const captionValues = !!valueAtX && extractCaptionTitleAndLink(valueAtX);
                const labelForTick = formatXAxisLabel(tick, this.userOptions.id, this.tickPositions, this.tickInterval, {
                    axisMin: this.min,
                    axisMax: this.max,
                    dataMin: this.dataMin,
                    dataMax: this.dataMax,
                }, this.userOptions.showInfinity, this.userOptions.showMinusInfinity, this.userOptions.infinityPlaceholderValue, captionValues, props);
                if (!uniqueLabels.has(labelForTick)) {
                    positionedTicks.push(tick);
                    uniqueLabels.add(labelForTick);
                }
            });
        }
    }
    if (axisName === AXIS_TYPES.Y) {
        const fontSize = this.userOptions.labels?.style?.fontSize;
        positionedTicks = handleTicksOverlap(positionedTicks, this.height, this.min, fontSize);
        if (positionedTicks &&
            positionedTicks.length === 1 &&
            positionedTicks[0] === 0) {
            if (this.options.startOnTick && this.min < 0) {
                positionedTicks.unshift(this.tickInterval * -1);
            }
            if (this.options.endOnTick && this.max > 0) {
                positionedTicks.push(this.tickInterval);
            }
        }
    }
    return positionedTicks && positionedTicks.length ? positionedTicks : null;
}
export function colorAxisFormatter() {
    const value = this.value;
    const props = this.chart.userOptions.chart.props;
    const valueColumn = props.allColumns[this.axis.userOptions.colorAxisColumn];
    const infinityLabel = formatInfinityValue(value, this.axis.tickInterval, valueColumn.columnHasInfinityValues, valueColumn.columnHasMinusInfinityValues, this.axis.userOptions.minValue, this.axis.userOptions.maxValue, this.axis.userOptions.max, AXIS_TYPES.COLOR);
    return (infinityLabel ||
        getLabelForNumericAxisOrColumn(value, valueColumn.format, [], valueColumn));
}
export function getTickPositionColorAxis() {
    const { tickInterval, tickPositions, width: colorAxisWidth, userOptions, chart, min, max, } = this;
    const props = chart.userOptions.chart.props;
    const valueColumn = props.allColumns[userOptions.colorAxisColumn];
    const userOptionsLabelStyles = userOptions.labels.style;
    const font = `${userOptionsLabelStyles.fontSize} ${userOptionsLabelStyles.fontFamily}`;
    const LABEL_PADDING = 80;
    const format = valueColumn.format;
    let intervalPrime;
    const uniqueLabelsForInfinity = new Set();
    const infinityLabelForFirstTick = formatInfinityValue(min, tickInterval, valueColumn.columnHasInfinityValues, valueColumn.columnHasMinusInfinityValues, userOptions.minValue, userOptions.maxValue, userOptions.max, AXIS_TYPES.COLOR);
    const infinityLabelForLastTick = formatInfinityValue(max, tickInterval, valueColumn.columnHasInfinityValues, valueColumn.columnHasMinusInfinityValues, userOptions.minValue, userOptions.maxValue, userOptions.max, AXIS_TYPES.COLOR);
    const unit = getUnitFromFormat(format);
    const newTickPositions = [];
    const minTickWithBuffer = min >= 0
        ? min - (min % tickInterval)
        : min - (min % tickInterval) - tickInterval;
    const maxTickWithBuffer = max > 0
        ? max - (max % tickInterval) + tickInterval
        : max - (max % tickInterval);
    const minTick = gradientBufferEnabled() ? minTickWithBuffer : min;
    const maxTick = gradientBufferEnabled() ? maxTickWithBuffer : max;
    const formattedValueOfLastTick = infinityLabelForLastTick ||
        getLabelForNumericAxisOrColumn(maxTick, valueColumn.format, [], valueColumn);
    const formattedValueOfFirstTick = infinityLabelForFirstTick ||
        getLabelForNumericAxisOrColumn(minTick, valueColumn.format, [], valueColumn);
    const decimals = decimalsForFormat(format, unit);
    const minInterval = minIntervalForTickPositions(format, unit, decimals);
    const tickLabelWidthOfFirstTick = getTextWidth(formattedValueOfFirstTick, font) + LABEL_PADDING;
    const tickLabelWidthOfLastTick = getTextWidth(formattedValueOfLastTick, font) + LABEL_PADDING;
    let maxTickLabelWidth = tickLabelWidthOfLastTick > tickLabelWidthOfFirstTick
        ? tickLabelWidthOfLastTick
        : tickLabelWidthOfFirstTick;
    if (minInterval > max && max > 0) {
        return [min];
    }
    if (maxTickLabelWidth > colorAxisWidth) {
        maxTickLabelWidth = colorAxisWidth;
    }
    if (tickPositions.length * maxTickLabelWidth < colorAxisWidth) {
        intervalPrime =
            tickInterval - (tickInterval % minInterval) + minInterval;
    }
    else {
        const tickAmount = Math.floor(colorAxisWidth / maxTickLabelWidth);
        const perTickLabelInterval = (maxTick - minTick) / tickAmount;
        const colorAxisReminder = perTickLabelInterval % minInterval;
        intervalPrime =
            colorAxisReminder !== 0
                ? perTickLabelInterval - colorAxisReminder + minInterval
                : perTickLabelInterval;
    }
    for (let i = minTick; i < maxTick + intervalPrime; i += intervalPrime) {
        const infinityLabel = formatInfinityValue(i, tickInterval, valueColumn.columnHasInfinityValues, valueColumn.columnHasMinusInfinityValues, userOptions.minValue, userOptions.maxValue, userOptions.max, AXIS_TYPES.COLOR);
        if (infinityLabel) {
            if (!uniqueLabelsForInfinity.has(infinityLabel)) {
                newTickPositions.push(i);
                uniqueLabelsForInfinity.add(infinityLabel);
            }
        }
        else {
            newTickPositions.push(i);
        }
    }
    return newTickPositions;
}
export const testHooks = {
    getMaxTickPosition,
    getMinTickPosition,
};
//# sourceMappingURL=highchart-formatter.js.map